import type { InferContractRouterOutputs } from '@orpc/contract'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { BaseContract } from 'pixai-contract/api/orpc'
import { useMyInfo } from '../apollo/auth'
import { orpc } from './contract'

export type ContentSafetyPreferences =
  InferContractRouterOutputs<BaseContract>['contentSafetyPreference']['getContentSafetyPreferences']

export const useContentSafetyPreferences = () => {
  const { data: me } = useMyInfo()
  return useQuery({
    ...orpc.contentSafetyPreference.getContentSafetyPreferences.queryOptions(),
    enabled: Boolean(me),
    meta: { dependsOn: ['login'] },
  })
}

export const useUpdateContentSafetyPreference = () => {
  // Pull the QueryClient at hook level instead of fishing it out of the
  // `onSuccess` 4th `MutationFunctionContext` arg — the latter is correct
  // for tanstack-query v5.90+ but the positional signature is easy to
  // misread and flagged by Copilot review (#11227). `useQueryClient()` is
  // the documented public access path and self-documenting at the call
  // site.
  const queryClient = useQueryClient()
  const queryKey =
    orpc.contentSafetyPreference.getContentSafetyPreferences.queryKey()

  return useMutation({
    ...orpc.contentSafetyPreference.updateContentSafetyPreference.mutationOptions(),
    onSuccess: async data => {
      const old = queryClient.getQueryData<ContentSafetyPreferences>(queryKey)
      if (old) {
        queryClient.setQueryData(queryKey, { ...old, ...data })
      } else {
        await queryClient.invalidateQueries({ queryKey })
      }
    },
  })
}
